import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import AiFeatureToolPrep from "../../../ai/shared/Popup/AiFeatureToolPrep";
import PassportSizeControls from "../../shared/controls/PassportSizeControls";
import PassportQualityPicker from "./PassportQualityPicker";
import PassportDeliverPanel from "./PassportDeliverPanel";
import PassportSheetPreview from "./PassportSheetPreview";
import PassportPhotoInstructionsPreview from "./PassportPhotoInstructionsPreview";
import PassportComplianceCheckPreview from "./PassportComplianceCheckPreview";
import AiFeaturePreview from "../../../ai/shared/Popup/AiFeaturePreview";
import ToolPrepDownloadFlowModals from "../../../shared/Popup/ToolPrepDownloadFlowModals";
import { useToolPrepDownloadFlow } from "../../../hooks/useToolPrepDownloadFlow";
import { downloadPassportPrintSheetPdf } from "../../../lib/downloadPassportPrintSheetPdf";
import { getPassportPresets } from "../../../../../services/api/catalog/passport_presets";
import {
  passportSuggestCrop,
  passportGenerate,
} from "../../../../../services/api/tools/passport_photo";
import {
  PASSPORT_CATEGORY_ID,
  resolvePassportSpec,
  sortPassportPresets,
  getPresetDisplayLabel,
  passportCountryLabel,
} from "../../../../../utils/passportPresetUtils";
import { resolveCreationImageUrl } from "../../../../../utils/resolveCreationImageUrl";
import { urlToFile } from "../../../../../utils/urlToFile";
import { usePricingCatalog } from "../../../../../hooks/app/usePricingCatalog";
import {
  DEFAULT_PASSPORT_QUALITY_ID,
  DEFAULT_PASSPORT_PREPARE_OPTION_ID,
  applyPassportCostsFromPricing,
  formatPassportQualityLabel,
  getPassportQualityTier,
  getPassportPrepareOption,
} from "../config/passportPhotoMakerFeatures";
import "./AiPassportPhotoMakerToolPrep.css";
const FLOW = {
  SETUP: "setup",
  DELIVER: "deliver",
};

const PASSPORT_THANK_YOU_COPY = {
  titleDownloading: "Downloading…",
  titleComplete: "Download complete",
  leadDownloading: "Please wait — we’re saving your file to this device.",
  leadComplete:
    "Your passport photo is saved on this computer or phone. You can find it in your Downloads folder.",
  primaryActionLabel: "Make new photo",
  secondaryActionLabel: "Share with friends",
};

/**
 * AI Photo Studio → AI Passport Photo Maker prep.
 * Setup → Deliver (print format + download). Enhancements are skipped.
 * Uses shared passport HTTP API only — not Trending / pages/passport.
 */
const AiPassportPhotoMakerToolPrep = ({ onCancel = () => {}, ...props }) => {
  const { getToken } = useAuth();
  const [countryOptions, setCountryOptions] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(true);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [qualityId, setQualityId] = useState(DEFAULT_PASSPORT_QUALITY_ID);
  const [flowStage, setFlowStage] = useState(FLOW.SETUP);
  const [prepareOptionId, setPrepareOptionId] = useState(
    DEFAULT_PASSPORT_PREPARE_OPTION_ID
  );
  const [suggestedCrop, setSuggestedCrop] = useState(null);
  const setErrorRef = useRef(null);
  const sizeControlsRef = useRef(null);
  const resultImageUrlRef = useRef(null);
  const clearResultRef = useRef(null);
  const [complianceActive, setComplianceActive] = useState(false);
  const { tools: pricingTools, audience: pricingAudience } = usePricingCatalog();

  const pricedPassport = useMemo(
    () => applyPassportCostsFromPricing(pricingTools, pricingAudience),
    [pricingTools, pricingAudience],
  );

  const sheetSizeIdRef = useRef("A4");
  const photoSizeRef = useRef({ w: 35, h: 45 });

  const downloadFlow = useToolPrepDownloadFlow({
    variant: "passport",
    sourceFileName: "passport-photo",
    fallbackDownloadName: "passport-photo.png",
    getToken,
    toolKey: "ai_passport_photo_maker",
    shareTitle: "AI Passport Photo Maker",
    shareText:
      "I just made my passport photo in minutes with AI Passport Photo Maker on LazyStudio — no photo booth needed!",
    thankYouCopy: PASSPORT_THANK_YOU_COPY,
    onAfterDigitalDownload: async (selection) => {
      if (selection.deliverableId !== "digital_print") return;
      const { w, h } = photoSizeRef.current;
      await downloadPassportPrintSheetPdf({
        imageUrl: selection.imageUrl,
        sheetSizeId: sheetSizeIdRef.current,
        photoWidthMm: w,
        photoHeightMm: h,
        fileName: "passport-print-sheet.pdf",
      });
    },
  });
  const { downloadBusy } = downloadFlow;

  const quality = useMemo(
    () => getPassportQualityTier(qualityId, pricedPassport.tiers),
    [qualityId, pricedPassport.tiers]
  );
  const passportSpec = useMemo(
    () => resolvePassportSpec(selectedPreset),
    [selectedPreset]
  );
  const photoWidthMm = Number(passportSpec?.w_mm) || 35;
  const photoHeightMm = Number(passportSpec?.h_mm) || 45;
  const complianceDocumentName = useMemo(() => {
    if (!selectedPreset) return "Passport photo";
    const sizeLabel = getPresetDisplayLabel(selectedPreset);
    const name = selectedPreset.name || selectedPreset.label || "Passport";
    return sizeLabel ? `${name} ${sizeLabel}` : name;
  }, [selectedPreset]);
  const complianceCountry = passportCountryLabel(selectedPreset);
  const complianceQualityLabel = useMemo(
    () =>
      formatPassportQualityLabel(
        photoWidthMm,
        photoHeightMm,
        quality?.id || DEFAULT_PASSPORT_QUALITY_ID
      ),
    [photoWidthMm, photoHeightMm, quality?.id]
  );
  const prepareOption = useMemo(
    () =>
      getPassportPrepareOption(prepareOptionId, {
        photoWidthMm,
        photoHeightMm,
      }),
    [prepareOptionId, photoWidthMm, photoHeightMm]
  );
  const sheetSizeId = prepareOption?.sheetSizeId || "A4";
  const showSheetPreview =
    flowStage === FLOW.DELIVER && Boolean(prepareOption?.print_sheet);

  const isDeliverFlow = flowStage === FLOW.DELIVER;

  const handleGetPrinted = useCallback(() => {
    setErrorRef.current?.(
      "Get it printed will be available soon. Download a digital copy for now."
    );
  }, []);

  const handleFooterGenerate = useCallback(() => {
    sizeControlsRef.current?.generate?.();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPresetsLoading(true);
    getPassportPresets()
      .then((res) => {
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data)) {
          const match = res.data.find(
            (c) => c.category_id === PASSPORT_CATEGORY_ID
          );
          const presets = match?.presets || res.data[0]?.presets || [];
          const sorted = sortPassportPresets(
            Array.isArray(presets) ? presets : []
          );
          setCountryOptions(sorted);
          setSelectedPreset(
            (prev) =>
              prev ||
              sorted.find((p) => p.is_default) ||
              sorted[0] ||
              null
          );
        } else {
          setCountryOptions([]);
        }
      })
      .catch(() => {
        if (!cancelled) setCountryOptions([]);
      })
      .finally(() => {
        if (!cancelled) setPresetsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const resetToSetup = useCallback((clearResult) => {
    setFlowStage(FLOW.SETUP);
    setComplianceActive(false);
    setSuggestedCrop(null);
    setPrepareOptionId(DEFAULT_PASSPORT_PREPARE_OPTION_ID);
    if (typeof clearResult === "function") clearResult();
  }, []);

  const handleCountryChange = useCallback((preset) => {
    setSelectedPreset(preset || null);
    setSuggestedCrop(null);
  }, []);

  const resolveInputFile = useCallback(async (resolvedFile, sourceImageUrl) => {
    if (resolvedFile) return resolvedFile;
    if (sourceImageUrl) {
      return urlToFile(sourceImageUrl, "passport-input.png");
    }
    return null;
  }, []);

  const handleMakeNewPhoto = useCallback(() => {
    downloadFlow.handleThankYouDismiss();
    resetToSetup(clearResultRef.current);
  }, [downloadFlow, resetToSetup]);

  const handleDownloadDigital = useCallback(async () => {
    const url = resultImageUrlRef.current;
    if (!url) return;
    sheetSizeIdRef.current = sheetSizeId;
    photoSizeRef.current = { w: photoWidthMm, h: photoHeightMm };
    await downloadFlow.openDownloadOptions({
      previewUrl: url,
      resolveImageUrl: async () => resultImageUrlRef.current || url,
    });
  }, [downloadFlow, photoHeightMm, photoWidthMm, sheetSizeId]);

  const resultFooterHint = isDeliverFlow
    ? prepareOption?.print_sheet
      ? `${prepareOption.label} sheet · ${prepareOption.copies} copies — download or get it printed.`
      : "Single image ready — download digital or get it printed."
    : undefined;

  const footerActionOrder = isDeliverFlow
    ? ["primary", "secondary"]
    : ["primary", "cancel"];

  const deliverDownloadLabel = (
    <span className="appm-footer-btn">
      <span className="appm-footer-btn__title">Download</span>
      <span className="appm-footer-btn__sub">Digital Copy</span>
    </span>
  );

  return (
    <>
    <AiFeatureToolPrep
      {...props}
      onCancel={onCancel}
      toolKey="ai_passport_photo_maker"
      title="Passport Photo"
      aka="AI Passport Photo Maker"
      bio="Create official passport and ID photos with AI."
      emptyPreviewLabel="Upload a portrait photo to preview your passport photo"
      loadingLabel="Creating passport photo…"
      emptyFooterHint="Upload a portrait, choose quality, then generate your passport photo."
      resultFooterHint={resultFooterHint}
      downloadFallbackName="passport-photo.png"
      showPrimaryAction={true}
      showOpenInEditor={false}
      showCreditBadge={false}
      showDownload={false}
      downloadLabel="Download"
      previewAspectRatio={
        photoWidthMm > 0 && photoHeightMm > 0
          ? photoWidthMm / photoHeightMm
          : null
      }
      extraBusy={downloadBusy}
      onSecondaryAction={isDeliverFlow ? handleGetPrinted : null}
      secondaryActionLabel={
        isDeliverFlow ? (
          <span className="appm-footer-btn">
            <span className="appm-footer-btn__title">Get it Printed</span>
            <span className="appm-footer-btn__sub">Delivered in 24hrs</span>
          </span>
        ) : null
      }
      secondaryActionDisabled={false}
      secondaryActionVariant="button"
      footerActionsLayout="row"
      footerPrimaryFirst={false}
      footerActionOrder={footerActionOrder}
      onPrimaryAction={
        isDeliverFlow ? handleDownloadDigital : handleFooterGenerate
      }
      primaryActionLabel={
        isDeliverFlow
          ? deliverDownloadLabel
          : quality?.credits != null
            ? `Generate · ${quality.credits} credits`
            : "Generate"
      }
      primaryActionDisabled={
        isDeliverFlow ? downloadBusy : !selectedPreset
      }
      renderPreview={(previewCtx) => {
        if (showSheetPreview) {
          return (
            <PassportSheetPreview
              imageUrl={previewCtx.resultImageUrl || previewCtx.imageUrl}
              loading={previewCtx.loading}
              loadingLabel={previewCtx.loadingLabel}
              emptyLabel={previewCtx.emptyLabel}
              sheetSizeId={sheetSizeId}
              photoWidthMm={photoWidthMm}
              photoHeightMm={photoHeightMm}
            />
          );
        }

        if (complianceActive || previewCtx.loading) {
          if (complianceActive) {
            return (
              <PassportComplianceCheckPreview
                active={complianceActive}
                documentName={complianceDocumentName}
                country={complianceCountry}
                documentType="Passport"
                qualityLabel={complianceQualityLabel}
                backgroundHex={passportSpec?.bg_hex || "#FFFFFF"}
                widthMm={photoWidthMm}
                heightMm={photoHeightMm}
              />
            );
          }
          return (
            <AiFeaturePreview
              imageUrl={previewCtx.imageUrl}
              loading={previewCtx.loading}
              loadingLabel={previewCtx.loadingLabel}
              emptyLabel={previewCtx.emptyLabel}
              transparent={previewCtx.transparent}
            />
          );
        }

        if (!previewCtx.sourceImageUrl) {
          return <PassportPhotoInstructionsPreview />;
        }

        return (
          <AiFeaturePreview
            imageUrl={previewCtx.imageUrl}
            loading={previewCtx.loading}
            loadingLabel={previewCtx.loadingLabel}
            emptyLabel={previewCtx.emptyLabel}
            transparent={previewCtx.transparent}
          />
        );
      }}
      renderControls={({
        busy,
        setError,
        clearResult,
        setResult,
        runLocalGenerate,
        sourceImageUrl,
        resolvedFile,
        hasResult,
        resultImageUrl,
      }) => {
        setErrorRef.current = setError;
        resultImageUrlRef.current = resultImageUrl;
        clearResultRef.current = clearResult;
        const controlsBusy = busy;
        const showDeliverStage = flowStage === FLOW.DELIVER && hasResult;

        if (showDeliverStage) {
          return (
            <div className="appm-controls">
              <PassportDeliverPanel
                prepareOptionId={prepareOptionId}
                photoWidthMm={photoWidthMm}
                photoHeightMm={photoHeightMm}
                busy={controlsBusy}
                onPrepareOptionChange={(id) => {
                  setPrepareOptionId(id);
                  setError(null);
                }}
                onBack={() => {
                  setFlowStage(FLOW.SETUP);
                  setError(null);
                }}
              />
            </div>
          );
        }

        return (
          <div className="appm-controls">
            <PassportSizeControls
              ref={sizeControlsRef}
              hideActions
              selectedPreset={selectedPreset}
              countryOptions={countryOptions}
              suggestedCrop={suggestedCrop}
              onCountryChange={(preset) => {
                handleCountryChange(preset);
                resetToSetup(clearResult);
              }}
              beforeActions={
                <PassportQualityPicker
                  selectedId={qualityId}
                  tiers={pricedPassport.tiers}
                  disabled={controlsBusy}
                  onSelect={(id) => {
                    setQualityId(id);
                    setError(null);
                  }}
                />
              }
              onSuggestCrop={async (opts) => {
                try {
                  await runLocalGenerate(async () => {
                    const token = await getToken();
                    if (!token) throw new Error("Sign in required.");
                    const file = await resolveInputFile(
                      resolvedFile,
                      sourceImageUrl
                    );
                    if (!file) {
                      throw new Error("Upload a portrait photo first.");
                    }
                    const data = await passportSuggestCrop(token, file, {
                      ...opts,
                      quality: quality?.id || DEFAULT_PASSPORT_QUALITY_ID,
                    });
                    if (!data?.success) {
                      throw new Error(
                        data?.message ||
                          data?.detail ||
                          "Passport suggest-crop failed."
                      );
                    }
                    setSuggestedCrop(data.result || null);
                    setError(null);
                  });
                } catch {
                  // error set by runLocalGenerate
                }
              }}
              onGenerate={async (opts) => {
                if (!sourceImageUrl && !resolvedFile) {
                  setError("Upload a portrait photo before generating.");
                  return;
                }
                if (!selectedPreset) {
                  setError("Select a passport country / size first.");
                  return;
                }
                setComplianceActive(true);
                try {
                  await runLocalGenerate(async () => {
                    const token = await getToken();
                    if (!token) throw new Error("Sign in required.");
                    const file = await resolveInputFile(
                      resolvedFile,
                      sourceImageUrl
                    );
                    if (!file) {
                      throw new Error("Upload a portrait photo first.");
                    }

                    let generateOpts = { ...opts };
                    delete generateOpts.dpi;
                    const passportQuality =
                      quality?.id || DEFAULT_PASSPORT_QUALITY_ID;
                    const needsCrop =
                      generateOpts.crop_left == null ||
                      generateOpts.crop_top == null ||
                      generateOpts.crop_w == null ||
                      generateOpts.crop_h == null;

                    if (needsCrop) {
                      const cropData = await passportSuggestCrop(token, file, {
                        w_mm: generateOpts.w_mm,
                        h_mm: generateOpts.h_mm,
                        head_h_ratio_min: generateOpts.head_h_ratio_min,
                        head_h_ratio_max: generateOpts.head_h_ratio_max,
                        eye_line_from_top: generateOpts.eye_line_from_top,
                        quality: passportQuality,
                      });
                      if (!cropData?.success) {
                        throw new Error(
                          cropData?.message ||
                            cropData?.detail ||
                            "Passport suggest-crop failed."
                        );
                      }
                      const crop = cropData.result || {};
                      setSuggestedCrop(crop);
                      if (
                        crop.crop_left != null &&
                        crop.crop_top != null &&
                        crop.crop_w != null &&
                        crop.crop_h != null
                      ) {
                        generateOpts = {
                          ...generateOpts,
                          crop_left: crop.crop_left,
                          crop_top: crop.crop_top,
                          crop_w: crop.crop_w,
                          crop_h: crop.crop_h,
                        };
                      }
                    }

                    const data = await passportGenerate(token, file, {
                      ...generateOpts,
                      quality: passportQuality,
                    });
                    if (!data?.success && !data?.output_url) {
                      throw new Error(
                        data?.message ||
                          data?.detail ||
                          "Passport generate failed."
                      );
                    }

                    const cid =
                      data.creation_id != null
                        ? Number(data.creation_id)
                        : null;
                    const { displayUrl, creationId: resolvedCid } =
                      await resolveCreationImageUrl(
                        token,
                        data.output_url,
                        cid
                      );

                    setResult(displayUrl, resolvedCid ?? cid);
                    setFlowStage(FLOW.DELIVER);
                    setPrepareOptionId(DEFAULT_PASSPORT_PREPARE_OPTION_ID);
                    setError(null);
                  });
                } catch {
                  // error set by runLocalGenerate
                } finally {
                  setComplianceActive(false);
                }
              }}
              busy={controlsBusy}
              presetsLoading={presetsLoading}
            />
            {hasResult ? (
              <button
                type="button"
                className="appm-deliver__link"
                disabled={controlsBusy}
                onClick={() => {
                  setFlowStage(FLOW.DELIVER);
                  setError(null);
                }}
              >
                Go to download
              </button>
            ) : null}
          </div>
        );
      }}
    />
    <ToolPrepDownloadFlowModals
      flow={downloadFlow}
      toolKey="ai_passport_photo_maker"
      onPrimaryAction={handleMakeNewPhoto}
      onBackToDashboard={onCancel}
      optionsTitle="Download"
    />
    </>
  );
};

export default AiPassportPhotoMakerToolPrep;
